/*
* BuroEcl
* Copyright (c) 2023 Buroclic. All rights reserved.
* 
* This file contains HTTP function allowing to communicate
* with both the global API (to retrieve cabinet's IP, logo etc.)
* and the cabinet's API through the reverse proxy
* It also contains functions that makes API Call and are useful for different files
*/

// Imports
import { NavigateFunction } from 'react-router';
import download from 'downloadjs';
import { toast } from 'react-toastify';

// Interfaces
import { Dossier } from '../../interfaces/IDossiers';
import { AlertType, TableRow } from '../../interfaces/IUtils';
import { Document, DownloadedDocument } from '../../interfaces/IDocuments';

// Utils
import { Error, ErrorForUsr, displayAlert, signOut } from './Errors';
import { getExtFromFilename } from './Format';
import { DepositDocument } from '../../interfaces/IDeposit';
import { Action } from '../../interfaces/IActions';

interface HTTPRequest {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    params?: pathParameter,
    body?: object,
    headers?: object
}

interface pathParameter {
    key: string,
    value: string
}

export interface HTTPResponse {
    error: string | null,
    result: object | null
}


export async function ApiCallConstructor(httpRequest: HTTPRequest, alias? : string): Promise<HTTPResponse> {
    const cabinet = JSON.parse(localStorage.getItem('cabinet') || "{}");
    const cabinetAlias = (alias ? alias : (localStorage.getItem('cabinetAlias') || ''));

    if (!alias && !cabinet.host) {
        displayAlert({ message: `Impossible de contacter le serveur du cabinet`, type: AlertType.error });
        console.error(cabinet);
    }

    let apiBaseUrl: string = `${window.origin}/externalapi/${cabinetAlias}/api/v1`;

    if (window.location.port !== '') // development mode
        apiBaseUrl = `${window.location.protocol}//${window.location.hostname}:3002/externalapi/${cabinetAlias}/api/v1`;

    /*let apiBaseUrl;
    if (cabinet.port && cabinet.port !== 443) apiBaseUrl = `${window.location.protocol}//${cabinet.host}:${cabinet.port}/buroecl/api/v1`;
    else apiBaseUrl = `${window.location.protocol}//${cabinet.host}/buroecl/api/v1`;*/
    //console.log(`Call to ${apiBaseUrl}${httpRequest.endpoint}`);

    return new Promise(async (resolve, reject) => {
        try {
            // Prepare HTTP Options
            const callOptions: any = {
                method: httpRequest.method,
                mode: 'cors',
                credentials: 'include',
                headers: {
                    ...httpRequest.headers || {}, // Add headers provided in params
                    'X-Api-Key': 'Q4aT7tY2mC5hF8oB1lN3cD6wG9zJ2vM5rP8eS1iK4gU7xY0vD3bR6yZ9aQ3mX6lC9hF2oL5qT8wY1cV4zJ7vB0kN3gC6fR9jS1iK4gU7xY0vD3bR6yZ9aQ3mX6lC9hF2oL5qT8wY1cV4zJ7vB0kN3gC6fR9j'
                }
            }

            // Handle Content-Type header
            const contentType: string = callOptions.headers['Content-Type'] ?? callOptions.headers['content-type'] ?? 'application/json';
            delete callOptions.headers['Content-Type'];
            callOptions.headers['content-type'] = contentType;

            // Add body if needed
            if (httpRequest.method !== 'GET') {
                // multipart/form-data
                if (httpRequest.body && contentType === 'multipart/form-data') {
                    callOptions.body = httpRequest.body; // add body
                    delete callOptions.headers['Content-Type']; // remove header to let fetch add the formData boundaries
                    delete callOptions.headers['content-type'];
                }

                // application/json (Default)
                else callOptions.body = JSON.stringify(httpRequest.body || {});
            }



            // Make the HTTP Call
            //const endpointLastChar :string = (httpRequest.endpoint.charAt(httpRequest.endpoint.length - 1) !== '/') ? '/' : '';
            //console.log(callOptions);
            const rawResponse = await fetch(`${apiBaseUrl}${httpRequest.endpoint}`, callOptions);

            // get result as JSON (whether it succeeded or not)
            let jsonResponse;
            try {
                jsonResponse = await rawResponse.json();
            } catch (error: any) {
                throw new window.Error('Unexpected end of JSON input : ' + rawResponse);
            }
            const error = jsonResponse ? jsonResponse.error ? jsonResponse.error : '' : '';

            switch (rawResponse.status) {
                case 200:
                    resolve({
                        error: null,
                        result: jsonResponse
                    });
                    break;
                case 400:
                    if (error === Error.incorrectBody)
                        reject({ error: ErrorForUsr.incorrectBody, result: null });
                    else
                        reject({ error: ErrorForUsr.badParameters, result: null });
                    break;
                case 401:
                    if (error === Error.NoMatch)
                        reject({ error: ErrorForUsr.NoMatch, result: null });
                    else {
                        reject({ error: ErrorForUsr.NotSignedIn, result: null });
                    }
                    break;
                case 403:
                    reject({ error: ErrorForUsr.Forbidden, result: null });
                    break;
                case 404:
                    reject({ error: ErrorForUsr.NotFound, result: null });
                    break;
                case 413:
                    reject({ error: ErrorForUsr.PayloadTooLarge, result: null });
                    break;
                case 415:
                    reject({ error: ErrorForUsr.UnsupportedMediaType, result: null });
                    break;
                case 429:
                    reject({ error: ErrorForUsr.TooManyRequests, result: null });
                    break;
                case 500:
                    reject({ error: ErrorForUsr.Unexpected, result: null });
                    break;
                default:
                    reject({
                        error: `${jsonResponse.error} - ${jsonResponse.message}`,
                        result: null
                    });
                    break;
            }
        } catch (error: any) {
            reject({
                error: `${error.error ? error.error + ' - ' : ''} ${error.message}`,
                result: null
            });
        }
    });
}



export async function GlobalApiCall(httpRequest: HTTPRequest): Promise<HTTPResponse> {
    let apiBaseUrl = `${window.origin}/api/v1`;

    if (window.location.port !== '') // development mode
        apiBaseUrl = `${window.location.protocol}//${window.location.hostname}:3002/api/v1`;

    //console.log(`Call to ${apiBaseUrl}${httpRequest.endpoint}`);

    return new Promise(async (resolve, reject) => {
        try {

            // Prepare HTTP Options
            const callOptions: any = {
                method: httpRequest.method,
                mode: 'cors',
                credentials: 'include',
            }

            // Add body if needed
            if (httpRequest.body) {
                callOptions.body = JSON.stringify(httpRequest.body);
                callOptions.headers = new Headers({ 'content-type': 'application/json', 'x-api-key': "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" });
            } else
                callOptions.headers = new Headers({ 'x-api-key': "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" });

            //console.log(callOptions);


            // Make the HTTP Call
            const rawResponse = await fetch(`${apiBaseUrl}${httpRequest.endpoint}`, callOptions);

            // get result as JSON (whether it succeeded or not)
            const jsonResponse = await rawResponse.json();
            const error = jsonResponse.error ? jsonResponse.error : '';

            switch (rawResponse.status) {
                case 200:
                    resolve({
                        error: null,
                        result: jsonResponse
                    });
                    break;
                case 400:
                    if (error === Error.incorrectBody)
                        reject({ error: ErrorForUsr.incorrectBody, result: null });
                    else
                        reject({ error: ErrorForUsr.badParameters, result: null });
                    break;
                case 401:
                    if (error === Error.NoMatch)
                        reject({ error: ErrorForUsr.NoMatch, result: null });
                    else
                        reject({ error: ErrorForUsr.NotSignedIn, result: null });
                    break;
                case 403:
                    reject({ error: ErrorForUsr.Forbidden, result: null });
                    break;
                case 404:
                    reject({ error: ErrorForUsr.NotFound, result: null });
                    break;
                case 500:
                    reject({ error: ErrorForUsr.Unexpected, result: null });
                    break;
                default:
                    reject({
                        error: `${jsonResponse.error} - ${jsonResponse.message}`,
                        result: null
                    });
                    break;
            }
        } catch (error: any) {
            reject({
                error: `${error.error ? error.error + ' - ' : ''} ${error.message}`,
                result: null
            });
        }
    });
}


/**********************************
 *       COMMON BACKEND CALLS     *
 **********************************/

/**
 * Retrieve folders/dossier the customer has permissions to see
 * Get it either from cache (localstorage) or through an API call
 * @author Mickaël "Naxe67" APPEL
 * @since 2024-05-02
 * @returns {Promise<Dossier[]>}
 */
export const fetchDossiers = async (): Promise<Dossier[]> => {
    try {
        // Retrieve it from localstorage
        if (localStorage.getItem('folders'))
            return JSON.parse(localStorage.getItem('folders') as string);

        // Retrieve it from backend
        const getDossiersResponse: HTTPResponse = await toast.promise(
            ApiCallConstructor({
                endpoint: '/me/dossiers',
                method: 'GET',
            }),
            {
                pending: `Récupération de vos dossiers en cours`,
                error: 'Impossible de récupérer vos dossiers'
            });

        const customerDossiers :Dossier[] = getDossiersResponse.result as Dossier[];
        localStorage.setItem('folders', JSON.stringify(customerDossiers));
        return customerDossiers;
    } catch (err: any) { throw err; }
}



/**
 * Downloads a single document from the server.
 * 
 * @param {object} selectedDoc - The selected document object containing the DOC_REF.
 * @returns {Promise<void>} - A Promise that resolves when the download is completed.
 */
export const downloadDocument = async (document: Document | TableRow, navigate: NavigateFunction) => {
    return new Promise(async (resolve, reject) => {
        try {
            const documentResponse: HTTPResponse = await ApiCallConstructor({
                endpoint: `/me/documents/${document.DOC_REF}`,
                method: 'GET'
            });

            const docData: DownloadedDocument = documentResponse.result as DownloadedDocument;

            // convert document base64 content to Blob in order to display it to users
            const byteCharacters = atob(docData.data);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }

            // Format data
            const docAsBlob = new Blob([byteArray], { type: 'application/octet-stream' });
            const nameToDisplay = docData.name + '.' + getExtFromFilename(docData.filename).toLowerCase();

            // Download the document through download-js
            download(docAsBlob, nameToDisplay);
            resolve(true);
        } catch (err: any) {
            // Handle redirection for session expiration
            if (err.error === ErrorForUsr.NotSignedIn)
                signOut((cabAlias) => navigate(`/${cabAlias}/login`));

            console.error(err.error)
            reject({ error: `Erreur lors du téléchargement de ${document?.DOC_NOM} : ${err.error ? err.error : err}`, result: null } as HTTPResponse);
        }
    });
}



/**
 * Get all documents that were once sent from the customer to the cabinet
 * @author Mickaël "Naxe67" APPEL
 * @since 2024-04-18
 * @returns {Promise<DepositDocument[]>}
 */
export const fetchSentDocuments = async (forceRefreshFromDB?: boolean): Promise<DepositDocument[]> => {
    return new Promise(async (resolve, reject) => {
        try {
            if (forceRefreshFromDB || (!localStorage.getItem('sentDocuments'))) {
                // API Call
                const getSentDocumentsResponse: HTTPResponse = await toast.promise(
                    ApiCallConstructor({
                        endpoint: `/me/deposits/documents`,
                        method: 'GET',
                    }),
                    {
                        pending: `Récupération de la liste de vos documents envoyés en cours`,
                        error: { render({ data }) { return `Erreur lors de la récupération de la liste de vos documents envoyés : ${(data as HTTPResponse).error}` } },
                    }
                );

                // Store sent documents
                const tmpSentDocuments: DepositDocument[] = getSentDocumentsResponse.result as DepositDocument[];
                localStorage.setItem('sentDocuments', JSON.stringify(tmpSentDocuments));
                resolve(tmpSentDocuments);

            } else {
                // Get sent documents from localStorage
                resolve(JSON.parse(localStorage.getItem('sentDocuments') || '{}') as DepositDocument[]);
            }
        } catch (err: any) {
            reject(err);
        }
    })
}



/**
 * Get new published documents from cache (localstorage) or backend.
 * New published documents means documents that has been published but hasn't been downloaded by the customer yet
 * @author Mickaël "Naxe67" APPEL
 * @since 2024-04-18
 * @returns {Promise<Document[]>}
 */
export const fetchNewDocs = async (): Promise<Document[]> => {
    try {
        // Retrieve documents from cache (localstorage)
        if (localStorage.getItem('newDocuments'))
            return (JSON.parse(localStorage.getItem('newDocuments') || '[]') as Document[]);

        console.log('Step A');

        // Retrieve documents from Back-End
        const getNewDocsResponse: HTTPResponse = await toast.promise(
            ApiCallConstructor({
                endpoint: `/me/documents/not-viewed`,
                method: 'GET',
            }),
            {
                pending: `Récupération des nouveaux documents en cours`,
                error: { render({ data }) { return `Erreur lors de la récupération des nouveaux documents : ${(data as HTTPResponse).error}` } },
            }
        );

        console.log('Step B : ', getNewDocsResponse);


        // Store newDocuments
        const newDocuments: Document[] = getNewDocsResponse.result as Document[];
        const jsonNewDocs :string = JSON.stringify(newDocuments);
        // localStorage only if less than 1 Mo
        if (jsonNewDocs.length < (2 * 1024 * 1024)) localStorage.setItem('newDocuments', jsonNewDocs);

        console.log('Step C : ', getNewDocsResponse);

        return newDocuments;
    } catch (err: any) { throw err; }
}



/**
 * Get new published actions from cache (localstorage) or backend.
 * New published actions means actions/events that has been published but hasn't been displayed yet to the customer
 * @author Mickaël "Naxe67" APPEL
 * @since 2024-05-02
 * @returns {Promise<Action[]>}
 */
export const fetchNewActions = async (): Promise<Action[]> => {
    try {
        // Retrieve new actions from cache (localstorage)
        if (localStorage.getItem('newActions'))
            return (JSON.parse(localStorage.getItem('newActions') || '[]') as Action[]);


        // Retrieve documents from Back-End
        const getNewActionsResponse: HTTPResponse = await toast.promise(
            ApiCallConstructor({
                endpoint: `/me/actions/not-viewed`,
                method: 'GET',
            }),
            {
                pending: `Récupération des nouveaux évènements en cours`,
                error: { render({ data }) { return `Erreur lors de la récupération des nouveaux évènements : ${(data as HTTPResponse).error}` } },
            }
        );

        // Store newDocuments
        const newActions: Action[] = getNewActionsResponse.result as Action[];
        localStorage.setItem('newActions', JSON.stringify(newActions));
        return newActions;
    } catch (err: any) { throw err; }
}



/**
 * Get upcoming actions from cache (localstorage) or backend.
 * Upcoming actions means actions/events that will take place in the future, whether or not they was already showed to the customer
 * @author Mickaël "Naxe67" APPEL
 * @since 2025-01-07
 * @returns {Promise<Action[]>}
 */
 export const fetchUpcomingActions = async (): Promise<Action[]> => {
    try {
        // Retrieve upcoming actions from cache (localstorage)
        if (localStorage.getItem('upcomingActions'))
            return (JSON.parse(localStorage.getItem('upcomingActions') || '[]') as Action[]);


        // Retrieve upcoming actions from Back-End
        const getUpcomingActionsResponse: HTTPResponse = await toast.promise(
            ApiCallConstructor({
                endpoint: `/me/actions/upcoming`,
                method: 'GET',
            }),
            {
                pending: `Récupération des évènements à venir en cours`,
                error: { render({ data }) { return `Erreur lors de la récupération des évènements à venir : ${(data as HTTPResponse).error}` } },
            }
        );

        // Store upcomingActions
        const upcomingActions: Action[] = getUpcomingActionsResponse.result as Action[];
        const jsonUpcomingActions :string = JSON.stringify(upcomingActions);
        // localStorage only if less than 1 Mo
        if (jsonUpcomingActions.length < (2 * 1024 * 1024)) localStorage.setItem('upcomingActions', jsonUpcomingActions);
        return upcomingActions;
    } catch (err: any) { throw err; }
}